import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Link, Redirect } from 'react-router-dom';
import useQuery from '../../hooks/use-query';
import Loader from '../loader';
import Paginator from '../paginator';
import api from '../../api';
import './static-page-list.css';

let searchUpdateTimeout = null;

const typesLabels = {
  'error-404': 'Erreur 404',
  'error-500': 'Erreur Interne',
  'folders': 'Nos Dossiers',
  'contact': 'Contact',
  'newsletter': 'Newsletter',
  'partners': 'Partenaires',
  'about': 'Qui sommes-nous ?',
  'legal': 'Mentions légales',
  'cookies': 'Cookies',
  'rss': 'Flux RSS',
  'comments-charter': 'Charte comm.',
};

const StaticPageType = ({ type }) => (
  <span className={`static-page-list_status-badge static-page-list_status-badge--${type}`}>
    {typesLabels[type] || type}
  </span>
);

const StaticPageList = () => {
  const query = useQuery();
  const page = query.get('page') ? query.get('page') : 1;
  const itemsPerPage = query.get('itemsPerPage') ? query.get('itemsPerPage') : 25;
  const search = query.get('search') ? query.get('search') : '';
  const sortBy = query.get('sortBy') ? query.get('sortBy') : 'updatedAt';
  const sortDir = query.get('sortDir') ? query.get('sortDir') : 'DESC';
  const generateRouteParams = useCallback(params => {
    let routeParams = [];

    if (params.search || (search && !params.hasOwnProperty('search'))) {
      routeParams.push(`search=${params.hasOwnProperty('search') ? params.search : search}`);
    }

    if (params.sortBy || (sortBy && !params.hasOwnProperty('sortBy'))) {
      routeParams.push(`sortBy=${params.hasOwnProperty('sortBy') ? params.sortBy : sortBy}`);
    }

    if (params.sortDir || (sortDir && !params.hasOwnProperty('sortDir'))) {
      routeParams.push(`sortDir=${params.hasOwnProperty('sortDir') ? params.sortDir : sortDir}`);
    }

    if (params.itemsPerPage || (itemsPerPage && !params.hasOwnProperty('itemsPerPage'))) {
      routeParams.push(`itemsPerPage=${params.hasOwnProperty('itemsPerPage') ? params.itemsPerPage : itemsPerPage}`);
    }

    if (params.page || (page && !params.hasOwnProperty('page'))) {
      routeParams.push(`page=${params.hasOwnProperty('page') ? params.page : page}`);
    }

    return `/pages-statiques?${routeParams.join('&')}`;
  }, [search, sortBy, sortDir, itemsPerPage, page]);
  const [redirect, setRedirect] = useState(null);
  const [staticPages, setStaticPages] = useState([]);
  const [pages, setPages] = useState(0);
  const [nbResults, setNbResults] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const changeSort = by => {
    let dir = by === sortBy ? sortDir === 'DESC' ? 'ASC' : 'DESC' : 'ASC';

    setRedirect(generateRouteParams({sortBy: by, sortDir: dir, page: 1}));
  };

  useEffect(() => {
    setRedirect(null);
    setIsLoading(true);
    api.staticPages.list(
      {
        search: search,
        page: page,
        items_per_page: itemsPerPage,
        sort_by: sortBy,
        sort_dir: sortDir,
      },
      data => {
        setStaticPages(data.items);
        setPages(data.pageCount);
        setNbResults(data.totalCount);
        setError(null);
        setIsLoading(false);
      },
      err => {
        setError(err);
        setIsLoading(false);
      },
    );
  }, [search, itemsPerPage, page, sortBy, sortDir]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <div className="static-page-list">
      <div className="static-page-list_nav">
        <div className="static-page-list_search">
          <input
            className="static-page-list_search-input"
            type="text"
            onChange={e => {
              let val = e.target.value;
              setSearchInput(val);
              clearTimeout(searchUpdateTimeout);
              searchUpdateTimeout = setTimeout(() => {
                setRedirect(generateRouteParams({search: val, page: 1}));
              }, 600);
            }}
            placeholder="Rechercher des pages-statiques"
            value={searchInput}
          />
        </div>

        <div className="static-page-list_pagination">
          <span className="static-page-list_count">Pages statiques {(page - 1) * itemsPerPage + 1} à {(page - 1) * itemsPerPage + Number(itemsPerPage, 10)} sur {nbResults} résultats</span>
          <select
            className="static-page-list_items-per-page"
            id="ipp"
            onChange={e => setRedirect(generateRouteParams({itemsPerPage: e.target.value, page: 1}))}
            value={itemsPerPage}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {isLoading && <Loader />}

      {!isLoading && error && <p className="static-page-list_error">Une erreur s'est produite, essayez de réactualiser la page.</p>}

      {!isLoading && !error && (
        <div>
          <div className="static-page-list_table">
            <div className="static-page-list_header">
              <div
                className="static-page-list_header-cell static-page-list_header-cell--sortable static-page-list_cell--id"
                onClick={() => changeSort('id')}
              >
                ID
                {sortBy === 'id' && (
                  <i className="static-page-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="static-page-list_header-cell static-page-list_header-cell--sortable static-page-list_cell--date"
                onClick={() => changeSort('updatedAt')}
              >
                Date de mise à jour
                {sortBy === 'updatedAt' && (
                  <i className="static-page-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="static-page-list_header-cell static-page-list_header-cell--sortable static-page-list_cell--type"
                onClick={() => changeSort('type')}
              >
                Type
                {sortBy === 'type' && (
                  <i className="static-page-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="static-page-list_header-cell static-page-list_header-cell--sortable static-page-list_cell--title"
                onClick={() => changeSort('title')}
              >
                Titre
                {sortBy === 'title' && (
                  <i className="static-page-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="static-page-list_header-cell static-page-list_header-cell--sortable static-page-list_cell--view"
                onClick={() => changeSort('nbView')}
              >
                Vues
                {sortBy === 'nbView' && (
                  <i className="static-page-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>
            </div>

            {staticPages.length > 0 && (
              <div>
                {staticPages.map(staticPage => (
                  <div
                    className="static-page-list_row"
                    key={staticPage.id}
                  >
                    <Link
                      className="static-page-list_cell static-page-list_cell--id"
                      title="Éditer la page"
                      to={`/pages-statiques/${staticPage.id}/modifier`}
                    >
                      {staticPage.id}
                    </Link>

                    <Link
                      className="static-page-list_cell static-page-list_cell--date"
                      title="Éditer la page"
                      to={`/pages-statiques/${staticPage.id}/modifier`}
                    >
                      {moment(staticPage.updatedAt).format('DD/MM/YYYY - HH:mm')}
                    </Link>

                    <Link
                      className="static-page-list_cell static-page-list_cell--type"
                      title="Éditer la page"
                      to={`/pages-statiques/${staticPage.id}/modifier`}
                    >
                      <StaticPageType type={staticPage.type} />
                    </Link>

                    <Link
                      className="static-page-list_cell static-page-list_cell--title"
                      title="Éditer la page"
                      to={`/pages-statiques/${staticPage.id}/modifier`}
                    >
                      {staticPage.title}
                    </Link>

                    <Link
                      className="static-page-list_cell static-page-list_cell--view"
                      title="Éditer la page"
                      to={`/pages-statiques/${staticPage.id}/modifier`}
                    >
                      {staticPage.nbView}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Paginator
            currentPage={page}
            maxPage={pages}
            routePattern={generateRouteParams({page: '[page]'})}
          />
        </div>
      )}

      {staticPages.length === 0 && !isLoading && (
        <p className="static-page-list_no-result">Aucun résultat.</p>
      )}
    </div>
  );
};

export default StaticPageList;
